import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { ArrowLeft, Recycle, Users, Trophy, Share2, Brain, CheckCircle2, MapPin, Calendar, Building2, Image as ImageIcon } from "lucide-react";

const ICON_MAP = { Recycle, Users };
const REGION_LABELS = { centro: "Zona Centro", norte: "Zona Norte", bajio: "Bajío", otros: "Otros estados" };
const VISIBLE_BADGE_KEYS = ["circular_economy_leader", "collaboration_master"];

function AnimatedNumber({ value, format = (v) => v, duration = 800 }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const start = performance.now();
    const from = display;
    const to = Number(value || 0);
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(from + (to - from) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line
  }, [value]);
  return <>{format(display)}</>;
}

export default function ProjectImpact() {
  const { id } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/projects/${id}/impact`).then(({ data }) => setData(data));
  }, [id]);

  if (!data) return <div className="text-sm text-[#5C6A7D]">Loading impact…</div>;

  const visibleBadges = (data.badges || []).filter((b) => VISIBLE_BADGE_KEYS.includes(b.key));
  const visibleEarnedCount = visibleBadges.filter((b) => b.earned).length;

  return (
    <div className="space-y-8" data-testid="project-impact-page">
      <Link to="/leaderboard" className="inline-flex items-center text-xs uppercase tracking-[0.18em] text-[#5C6A7D] hover:text-[#0A2540]">
        <ArrowLeft size={14} className="mr-1" /> Leaderboard
      </Link>

      {/* Hero banner */}
      <div className="relative rounded-3xl overflow-hidden border border-[#E2E8F0] min-h-[240px] flex items-end" data-testid="project-hero">
        {data.project.hero_image ? (
          <img src={data.project.hero_image} alt={data.project.name} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-[#EAF0F6]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540]/90 via-[#0A2540]/40 to-transparent" />
        <div className="relative z-10 p-6 sm:p-8 w-full flex flex-wrap items-end justify-between gap-4 text-white">
          <div>
            {data.project.client && <div className="text-[10px] uppercase tracking-[0.22em] text-white/70">Cliente · {data.project.client}</div>}
            <h1 className="font-display text-3xl sm:text-5xl font-extrabold tracking-tighter mt-1">{data.project.name}</h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-sm text-white/85">
              {data.project.region && <span className="inline-flex items-center gap-1.5"><MapPin size={14} /> {REGION_LABELS[data.project.region] || data.project.region}</span>}
              <span className="inline-flex items-center gap-1.5"><Building2 size={14} /> {data.project.location || "—"}</span>
              {data.project.start_date && <span className="inline-flex items-center gap-1.5"><Calendar size={14} /> {data.project.start_date} → {data.project.estimated_completion || "—"}</span>}
              <span className="uppercase tracking-[0.18em] font-semibold text-[#7EE2A8]">{data.project.status}</span>
            </div>
          </div>
          <div className="bg-white/12 backdrop-blur-sm px-5 py-3 rounded-2xl flex items-center gap-3" data-testid="project-points">
            <Trophy size={26} strokeWidth={1.5} className="text-[#FBBF24]" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/60">Puntos totales</div>
              <div className="font-display font-extrabold text-3xl leading-none"><AnimatedNumber value={data.points} format={(v) => Math.round(v).toLocaleString()} /></div>
            </div>
          </div>
        </div>
      </div>

      {/* Team members */}
      {data.team?.length > 0 && (
        <div data-testid="project-team">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#5C6A7D] mb-3 flex items-center gap-1.5"><Users size={13} /> Equipo del proyecto</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {data.team.map((m) => (
              <Link key={m.role_label} to={`/employees/${m.id}`} className="bg-white border border-[#E2E8F0] rounded-2xl p-4 flex flex-col items-center text-center transition-all duration-300 hover:-translate-y-1 hover:border-[#16A34A]/30 hover:shadow-[0_12px_30px_rgba(22,163,74,0.10)]" data-testid={`team-member-${m.id}`}>
                <Avatar name={m.name} photo={m.photo} size={52} />
                <div className="font-semibold text-sm text-[#0A2540] mt-3 leading-tight">{m.name}</div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-[#5C6A7D] mt-1">{m.role_label}</div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {[
          { label: "Activos reutilizados", value: data.assets_reused, icon: Recycle, color: "#0052CC" },
          { label: "Activos compartidos", value: data.assets_shared, icon: Share2, color: "#7C3AED" },
          { label: "Compras evitadas", value: data.purchases_avoided, icon: Brain, color: "#E11D48" },
        ].map((k) => (
          <div key={k.label} className="bg-white border border-[#E2E8F0] p-6 rounded-sm">
            <div className="flex items-center justify-between text-[#5C6A7D]">
              <div className="text-[10px] uppercase tracking-[0.2em]">{k.label}</div>
              <k.icon size={16} strokeWidth={1.5} style={{ color: k.color }} />
            </div>
            <div className="kpi-value text-3xl sm:text-4xl text-[#0A2540] mt-3">{typeof k.value === "number" ? <AnimatedNumber value={k.value} format={(v) => Math.round(v).toLocaleString()} /> : k.value}</div>
          </div>
        ))}
      </div>

      <div className="grid gap-6">
        {/* Progress card */}
        <div className="bg-white border border-[#E2E8F0] rounded-sm p-6 space-y-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C6A7D]">Performance</div>
          <div>
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-[#0A2540]">Reutilización vs nuevas adquisiciones</div>
              <div className="font-mono text-[#0A2540] font-bold">{data.reuse_percentage}%</div>
            </div>
            <div className="h-2 bg-[#EAF0F6] mt-2 overflow-hidden">
              <div className="h-full bg-[#10B981] transition-all duration-700" style={{ width: `${data.reuse_percentage}%` }} />
            </div>
            <div className="text-xs text-[#5C6A7D] mt-1.5">{data.assets_reused + data.purchases_avoided} reutilizados/evitados · {data.total_assets} activos totales en el proyecto</div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-[#0A2540]">Puntaje de sustentabilidad</div>
              <div className="font-mono text-[#0A2540] font-bold">{data.sustainability_score}/100</div>
            </div>
            <div className="h-2 bg-[#EAF0F6] mt-2 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#0052CC] to-[#10B981] transition-all duration-700" style={{ width: `${data.sustainability_score}%` }} />
            </div>
            <div className="text-xs text-[#5C6A7D] mt-1.5">Ponderado entre reutilización, intercambio, sobrantes y compras evitadas.</div>
          </div>
          <div className="pt-3 border-t border-[#E2E8F0]">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C6A7D]">Sobrantes recuperados</div>
            <div className="font-display font-extrabold text-[#0A2540] text-2xl mt-1">{data.surplus_uploaded}</div>
          </div>
        </div>
      </div>

      {/* Gallery */}
      {data.project.gallery?.length > 0 && (
        <div data-testid="project-gallery">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#5C6A7D] mb-3 flex items-center gap-1.5"><ImageIcon size={13} /> Galería del proyecto</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {data.project.gallery.map((src, i) => (
              <div key={i} className="aspect-[4/3] rounded-2xl overflow-hidden border border-[#E2E8F0] group">
                <img src={src} alt={`${data.project.name} ${i + 1}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Milestones timeline removed per Sprint 5C */}

      {/* Badges */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#5C6A7D]">Achievements</div>
            <div className="font-display text-2xl font-bold text-[#0A2540] mt-0.5">Insignias del proyecto</div>
          </div>
          <div className="text-sm text-[#5C6A7D]">{visibleEarnedCount} de {visibleBadges.length} ganadas</div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4" data-testid="badges-grid">
          {visibleBadges.map((b) => {
            const Icon = ICON_MAP[b.icon] || Trophy;
            return (
              <div key={b.key} className={`p-5 rounded-sm border transition-all duration-300 ${b.earned ? "bg-white border-[#0A2540]/30 shadow-sm" : "bg-[#FAFAFA]/60 border-[#E2E8F0]"}`} data-testid={`badge-${b.key}`}>
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-sm flex items-center justify-center flex-shrink-0" style={{ background: b.earned ? `${b.color}18` : "#EAF0F6", color: b.earned ? b.color : "#5C6A7D" }}>
                    <Icon size={22} strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className={`font-display font-bold ${b.earned ? "text-[#0A2540]" : "text-[#5C6A7D]"}`}>{b.label}</div>
                      {b.earned && <CheckCircle2 size={14} className="text-[#10B981]" />}
                    </div>
                    <div className="text-xs text-[#5C6A7D] mt-0.5">{b.description}</div>
                    {!b.earned && (
                      <div className="mt-3">
                        <div className="h-1 bg-[#EAF0F6] overflow-hidden">
                          <div className="h-full transition-all duration-500" style={{ width: `${Math.round(b.progress * 100)}%`, background: b.color }} />
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.15em] text-[#5C6A7D] mt-1">{Math.round(b.progress * 100)}% para desbloquear</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
